import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Checkbox,
  TextField,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiX,
} from "react-icons/fi";
import { donationSchemasAPI } from "../services/api";
import DonationSchemaDialog from "../components/DonationSchemaDialog";
import TableLoader from "../components/TableLoader";
import { colors } from "../theme/modernTheme";
import { useMultiSelect } from "../hooks/useMultiSelect";
import { useDebounce } from "../hooks/useDebounce";
import BulkActionToolbar from "../components/BulkActionToolbar";
import BulkDeleteDialog from "../components/BulkDeleteDialog";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import { showSuccess, showError } from "../utils/toast";
import { Toaster } from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { MODULE_PERMISSIONS } from "../utils/modulePermissions";

export default function DonationSchemas() {
  const { hasWritePermission } = useAuth();
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [schemaToDelete, setSchemaToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // RBAC: Check if user has write permission for donation schemes
  const canWrite = hasWritePermission(MODULE_PERMISSIONS.DONATION_SCHEME);
  const canDelete = canWrite; // Delete requires write permission

  // Search and pagination states
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 500);
  const [page, setPage] = useState(0);
  const rowsPerPage = 12;
  const [totalCount, setTotalCount] = useState(0);

  // Multi-select state
  const multiSelect = useMultiSelect(schemas, totalCount, "id");
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Custom toggleSelectAll that excludes schemas with can_delete === false
  const handleToggleSelectAll = () => {
    if (multiSelect.isAllCurrentPageSelected) {
      // If all are selected, clear selection
      multiSelect.clearSelection();
    } else {
      // Clear first, then select only schemas that can be deleted
      multiSelect.clearSelection();
      const deletableSchemas = schemas.filter(
        (schema) => schema.can_delete !== false
      );

      deletableSchemas.forEach((schema) => {
        multiSelect.toggleSelect(schema);
      });
    }
  };

  useEffect(() => {
    loadSchemas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, page]);

  // Reset to page 0 when search changes
  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const loadSchemas = async () => {
    setLoading(true);
    try {
      const params = {
        search: debouncedSearch,
        page: page + 1,
        limit: rowsPerPage,
        sortBy: "name",
        sortOrder: "asc",
      };

      const response = await donationSchemasAPI.getAll(params);
      setSchemas(response.data);
      setTotalCount(response.pagination?.total || response.data.length);
    } catch (error) {
      showError("Failed to load donation schemas");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedSchema(null);
    setDialogOpen(true);
  };

  const handleEdit = (schema) => {
    setSelectedSchema(schema);
    setDialogOpen(true);
  };

  const handleDeleteClick = (schema) => {
    if (schema.can_delete === false) {
      showError("Cannot delete this schema as it is being used in donations");
      return;
    }
    setSchemaToDelete(schema);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await donationSchemasAPI.delete(schemaToDelete.id);
      multiSelect.removeFromSelection(schemaToDelete.id);
      showSuccess("Donation schema deleted successfully");
      setDeleteDialogOpen(false);
      setSchemaToDelete(null);
      loadSchemas();
    } catch (error) {
      showError(error.error?.message || "Failed to delete donation schema");
    } finally {
      setDeleting(false);
    }
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setPage(0);
    multiSelect.clearSelection();
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalCount / rowsPerPage);
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const selectionData = multiSelect.getSelectionData();

      const filters = {};
      if (debouncedSearch) filters.search = debouncedSearch;

      await donationSchemasAPI.bulkDelete({
        ...selectionData,
        filters,
      });

      setBulkDeleteDialogOpen(false);
      multiSelect.clearSelection();
      loadSchemas();
      showSuccess(
        `Successfully deleted ${multiSelect.selectedCount} donation schema(s)`
      );
    } catch (error) {
      showError(error?.error?.message || "Failed to delete donation schemas");
    } finally {
      setBulkDeleting(false);
    }
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  return (
    <Box>
      <Toaster />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={1}
        mb={1}>
        <Typography
          variant="h4"
          sx={{
            fontSize: "1.5rem",
            fontWeight: 700,
            color: colors.text.primary,
          }}>
          Donation Schemes
        </Typography>
        {canWrite && (
          <Button
            variant="contained"
            startIcon={<FiPlus />}
            onClick={handleAdd}
            sx={{
              borderRadius: "4px",
              backgroundColor: colors.primary.main,
              "&:hover": { backgroundColor: colors.primary.dark },
            }}>
            Add Scheme
          </Button>
        )}
      </Box>

      {/* Search and Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "4px",
          boxShadow: "none",
          opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
          pointerEvents: multiSelect.selectedCount > 0 ? "none" : "auto",
        }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search donation schemas..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              InputProps={{
                endAdornment: searchInput ? (
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    disabled={multiSelect.selectedCount > 0}
                    sx={{ padding: "4px" }}>
                    <FiX size={16} style={{ color: colors.text.secondary }} />
                  </IconButton>
                ) : (
                  <FiSearch
                    style={{ marginRight: 8, color: colors.text.secondary }}
                  />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.background.paper,
                  borderRadius: "4px",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
              }}>
              {searchInput && (
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  disabled={multiSelect.selectedCount > 0}
                  sx={{
                    backgroundColor: "transparent",
                    color: colors.text.secondary,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}>
                  Clear
                </Button>
              )}
              <IconButton
                size="small"
                onClick={handlePreviousPage}
                disabled={page === 0 || multiSelect.selectedCount > 0}
                sx={{
                  marginRight: -2,
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: colors.action.hover,
                  },
                }}>
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: "100px",
                  textAlign: "center",
                }}>
                Page {page + 1} of {totalPages || 1}
              </Typography>
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={
                  page >= totalPages - 1 || multiSelect.selectedCount > 0
                }
                sx={{
                  marginLeft: -2,
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: colors.action.hover,
                  },
                }}>
                <FiChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={multiSelect.selectedCount}
        onClearSelection={multiSelect.clearSelection}
        onDelete={() => setBulkDeleteDialogOpen(true)}
        canDelete={canDelete}
        itemName="schemas"
        showSelectAllPage={true}
        onSelectAllPage={handleToggleSelectAll}
        isAllPageSelected={multiSelect.isAllCurrentPageSelected}
      />

      {loading ? (
        <TableLoader message="Loading donation schemas..." />
      ) : schemas.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            color: colors.text.secondary,
          }}>
          <Typography variant="body1">
            {searchInput
              ? "No donation schemas found"
              : "No donation schemas yet"}
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {schemas.map((schema) => {
              const isSelected = multiSelect.isSelected(schema);
              return (
                <Grid item xs={12} sm={6} md={4} key={schema.id}>
                  <Card
                    sx={{
                      position: "relative",
                      borderRadius: "4px",
                      boxShadow: "none",
                      border: `1px solid ${colors.border.main}`,
                      backgroundColor: multiSelect.isSelected(schema)
                        ? colors.primary.main + "10"
                        : "transparent",
                      transition: "all 0.1s ease-in-out",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                        "& .action-icons": {
                          opacity: 1,
                          visibility: "visible",
                        },
                      },
                    }}>
                    <CardContent
                      sx={{ backgroundColor: colors.background.paper }}>
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <Tooltip
                          title={
                            schema.can_delete === false
                              ? "Cannot delete: schemes is used in donations"
                              : ""
                          }
                          arrow
                          placement="top">
                          <span>
                            <Checkbox
                              size="medium"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                multiSelect.toggleSelect(schema);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              disabled={schema.can_delete === false}
                              sx={{
                                padding: "4px",
                                "& .MuiSvgIcon-root": { fontSize: 28 },
                                transform: "scale(1.2)",
                                color:
                                  schema.can_delete === false
                                    ? colors.text.disabled
                                    : colors.text.secondary,
                                "&.Mui-checked": {
                                  color: colors.primary.main,
                                },
                                "&.Mui-disabled": {
                                  opacity: 0.4,
                                  cursor: "not-allowed",
                                },
                              }}
                            />
                          </span>
                        </Tooltip>
                        <Box flex={1}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: "0.9rem",
                              fontWeight: 600,
                              color: colors.text.primary,
                              mb: 0.5,
                              textTransform: "capitalize",
                            }}>
                            {schema.name}
                          </Typography>
                          <Tooltip
                            title={schema.description || "No description"}
                            arrow
                            placement="bottom">
                            <Typography
                              variant="body2"
                              sx={{
                                color: colors.text.secondary,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                lineHeight: "1.4em",
                                height: "2.8em",
                              }}>
                              {schema.description || "No description"}
                            </Typography>
                          </Tooltip>
                        </Box>
                        {multiSelect.selectedCount > 0
                          ? null
                          : canWrite && (
                              <Box
                                className="action-icons"
                                display="flex"
                                gap={0.5}
                                sx={{
                                  opacity: 0,
                                  visibility: "hidden",
                                  transition: "all 0.2s ease-in-out",
                                }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(schema)}
                                  sx={{
                                    color: colors.text.secondary,
                                    "&:hover": {
                                      color: colors.primary.main,
                                      backgroundColor: "transparent",
                                    },
                                  }}>
                                  <FiEdit2 size={16} />
                                </IconButton>
                                {canDelete && (
                                  <Tooltip
                                    title={
                                      schema.can_delete === false
                                        ? "Cannot delete: schemes is used in donations"
                                        : "Delete"
                                    }
                                    arrow>
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleDeleteClick(schema)
                                        }
                                        disabled={schema.can_delete === false}
                                        sx={{
                                          color:
                                            schema.can_delete === false
                                              ? colors.text.disabled
                                              : colors.text.secondary,
                                          "&:hover": {
                                            color: colors.error.main,
                                            backgroundColor: "transparent",
                                          },
                                          "&.Mui-disabled": {
                                            opacity: 0.4,
                                            cursor: "not-allowed",
                                          },
                                        }}>
                                        <FiTrash2 size={16} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>
                            )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Bottom Pagination */}
      {!loading && schemas.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 2,
            gap: 1,
            opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
            pointerEvents: multiSelect.selectedCount > 0 ? "none" : "auto",
          }}>
          <IconButton
            size="small"
            onClick={handlePreviousPage}
            disabled={page === 0 || multiSelect.selectedCount > 0}
            sx={{
              marginRight: -2,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: colors.action.hover,
              },
              "&:disabled": {
                opacity: 0.5,
                cursor: "not-allowed",
              },
            }}>
            <FiChevronLeft />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              minWidth: "100px",
              textAlign: "center",
            }}>
            Page {page + 1} of {totalPages || 1}
          </Typography>
          <IconButton
            size="small"
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || multiSelect.selectedCount > 0}
            sx={{
              marginLeft: -2,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: colors.action.hover,
              },
              "&:disabled": {
                opacity: 0.5,
                cursor: "not-allowed",
              },
            }}>
            <FiChevronRight />
          </IconButton>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <DonationSchemaDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        schema={selectedSchema}
        onSuccess={loadSchemas}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Donation Schema"
        message={`Are you sure you want to delete "${schemaToDelete?.name}"? This action cannot be undone.`}
        itemName="donation schema"
        deleting={deleting}
      />

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        count={multiSelect.selectedCount}
        itemName="donation scheme"
        deleting={bulkDeleting}
      />
    </Box>
  );
}
